import { Component, OnInit, effect, inject, ElementRef, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { SharedSignalsService } from '../../../common/services/shared-signals.service';
import { PartnerService } from '../../service/partner.service';
import { CheckoutService } from '../../../checkout/services/checkout.service';
import { regexConstant } from '../../../common/constants/regex-pattern.constant';
import { MessageService } from 'primeng/api';
import { Country } from '../../../common/models/country.model';
import { Category } from '../../../common/models/category.model';
import { BusinessType } from '../../../common/models/business-type.model';
import { Platform } from '../../../common/models/platform.model';
import { PartnerRequest } from '../../../common/models/partner-request.model';

type PartnerRequestForm = FormGroup<{
  businessName: FormControl<string | null>;
  businessNameArabic: FormControl<string | null>;
  country: FormControl<Country | null>;
  busines_type: FormControl<BusinessType | null>;
  emailAddress: FormControl<string | null>;
  platform: FormControl<Platform | null>;
  webURL: FormControl<string | null>;
  otherPlatfom: FormControl<string | null>;
  contactPersonFullName: FormControl<string | null>;
  mobileNumber: FormControl<string | null>;
  bankAccountHolderName: FormControl<string | null>;
  iban: FormControl<string | null>;
  brandName: FormControl<string | null>;
  category: FormControl<Category | null>;
  otherIndustry: FormControl<string | null>;
  bankName: FormControl<string | null>;
  bankAccountNumber: FormControl<string | null>;
  logoFile: FormControl<string | null>;
  featuredImage: FormControl<string | null>;
  isShareHolder: FormControl<boolean | null>;
  comments: FormControl<string | null>;
}>;

@Component({
  selector: 'taly-request-form',
  templateUrl: './request-form.component.html',
  styleUrls: ['./request-form.component.scss'],
  standalone: false,
})
export class RequestFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private partnerService = inject(PartnerService);
  private elementRef = inject(ElementRef);
  private checkoutService = inject(CheckoutService);
  private messageService = inject(MessageService);

  transferSignal = inject(SharedSignalsService);
  partnerRequestForm!: PartnerRequestForm;
  displayDialog: boolean = false;
  isTermOfUse: boolean = false;
  isformSubmit: boolean = false;
  countries: any[] = [];
  businessType: any[] = [];
  platForms: any[] = [];
  categories: any[] = [];
  textDirection!: string;

  // Image upload (matching edit-merchant pattern)
  logoFile!: string; // Base64 data for preview and API
  featuredImageFile!: string; // Base64 data for preview and API
  maxFileSize: number = 500 * 1024; // 500KB
  uploadedFiles: Record<string, string> = {};

  // 3-step flow state
  activeStep: number = 0;
  interestRequestId: number | null | undefined = null;
  applicationEditToken: string | null = null;
  submitting: boolean = false;
  updating: boolean = false;

  // Dynamic field visibility
  isNotInStore: boolean = false;
  isOtherPlatform: boolean = false;
  isOtherIndustry: boolean = false;

  @Input() nextCallback!: () => void;

  get f() {
    return this.partnerRequestForm.controls;
  }

  constructor() {
    // Initialize form with all fields
    this.partnerRequestForm = this.fb.group({
      // Step 1: Required fields
      businessName: ['', [Validators.required, Validators.minLength(3)]],
      businessNameArabic: [''],
      country: this.fb.control<Country | null>(null, [Validators.required]),
      busines_type: this.fb.control<BusinessType | null>(null, [Validators.required]),
      emailAddress: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(256),
          Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/),
        ],
      ],
      // Dynamic fields for Step 1 (when businessType = Online/Both)
      platform: this.fb.control<Platform | null>(null),
      webURL: this.fb.control<string | null>(null),
      otherPlatfom: [''],
      // Step 1: Contact Information (required)
      contactPersonFullName: ['', [Validators.required, Validators.minLength(2)]],
      mobileNumber: this.fb.control<string | null>(null, [
        Validators.required,
        Validators.pattern(regexConstant.KUWAIT_MOBILE_NUMBER),
        Validators.maxLength(8),
        Validators.minLength(8),
      ]),
      // Step 1: Banking Information (optional)
      bankAccountHolderName: [''],
      iban: [''],

      // Step 2: Optional fields
      brandName: [''],
      category: this.fb.control<Category | null>(null, [Validators.required]),
      otherIndustry: [''],
      bankName: [''],
      bankAccountNumber: [''],
      logoFile: [''], // For error handling (matching edit-merchant pattern)
      featuredImage: [''], // For error handling (matching edit-merchant pattern)
      isShareHolder: [false],
      comments: this.fb.control<string | null>(null, [Validators.maxLength(256)]),
    });

    effect(() => {
      this.textDirection = this.transferSignal.textDirection();
    });
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.getCountries();
    this.getBusinessType();
    this.getPlatform();
    this.getCategory();

    // Business type changes: set dynamic validators
    this.partnerRequestForm.get('busines_type')?.valueChanges.subscribe((val) => {
      this.onBusinessTypeChange(val?.nameEn);
    });

    // Platform -> otherPlatfom validator
    this.partnerRequestForm.get('platform')?.valueChanges.subscribe((val) => {
      this.onPlatformChange(val);
    });

    // Category -> otherIndustry validator
    this.partnerRequestForm.get('category')?.valueChanges.subscribe((val) => {
      this.onCategoryChange(val);
    });
  }

  // ========== Dynamic Validation Methods ==========

  onBusinessTypeChange(type: string | undefined) {
    const typeNormalized = (type || '').toString().toLowerCase();
    const webCtrl = this.partnerRequestForm.get('webURL');
    const platformCtrl = this.partnerRequestForm.get('platform');

    if (typeNormalized === 'online' || typeNormalized === 'both') {
      this.isNotInStore = true;
      webCtrl?.setValidators([Validators.required]);
      platformCtrl?.setValidators([Validators.required]);
    } else {
      this.isNotInStore = false;
      webCtrl?.clearValidators();
      platformCtrl?.clearValidators();
      webCtrl?.setErrors(null);
      platformCtrl?.setErrors(null);
      webCtrl?.markAsPristine();
      platformCtrl?.markAsPristine();
      webCtrl?.markAsUntouched();
      platformCtrl?.markAsUntouched();
    }

    webCtrl?.updateValueAndValidity();
    platformCtrl?.updateValueAndValidity();
  }

  onPlatformChange(platform: any) {
    this.isOtherPlatform = !!(platform?.nameEn === 'Other');
    const ctrl = this.partnerRequestForm.get('otherPlatfom');
    if (this.isOtherPlatform) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
      ctrl?.setErrors(null);
    }
    ctrl?.updateValueAndValidity();
  }

  onCategoryChange(category: any) {
    this.isOtherIndustry = !!(category?.nameEn === 'Other');
    const ctrl = this.partnerRequestForm.get('otherIndustry');
    if (this.isOtherIndustry) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
      ctrl?.setErrors(null);
    }
    ctrl?.updateValueAndValidity();
  }

  // ========== Data Fetching ==========

  getCountries() {
    this.partnerService.getCountries().subscribe(
      (res) => {
        this.countries = res?.filter((elem) => elem?.status == '1');
      },
      (err) => {
        this.transferSignal.showMessage('error', err?.message || err);
      }
    );
  }

  getCategory() {
    this.partnerService.getCategories().subscribe(
      (res) => {
        this.categories = res?.filter((elem) => elem?.status == '1');
      },
      (err) => {
        this.transferSignal.showMessage('error', err?.message || err);
      }
    );
  }

  getBusinessType() {
    this.partnerService.getBusinessType().subscribe(
      (res) => {
        const priority: Record<string, number> = { Online: 1, Store: 2, Both: 3 };
        this.businessType = res
          ?.filter((elem) => elem?.status === '1')
          ?.sort((a, b) => priority[a.nameEn] - priority[b.nameEn]);
      },
      (err) => {
        this.transferSignal.showMessage('error', err?.message || err);
      }
    );
  }

  getPlatform() {
    this.partnerService.getPlatform().subscribe(
      (res) => {
        this.platForms = res?.filter((elem) => elem?.status == '1');
      },
      (err) => {
        this.transferSignal.showMessage('error', err?.message || err);
      }
    );
  }

  // ========== Step 1: Submit Form (Required Fields) ==========

  isStep1Valid(): boolean {
    const requiredFields = [
      'businessName',
      'country',
      'busines_type',
      'category',
      'emailAddress',
      'contactPersonFullName',
      'mobileNumber',
    ];

    if (this.isOtherIndustry) {
      requiredFields.push('otherIndustry');
    }

    // Check dynamic fields if business type is Online/Both
    if (this.isNotInStore) {
      requiredFields.push('webURL', 'platform');
      if (this.isOtherPlatform) {
        requiredFields.push('otherPlatfom');
      }
    }

    return requiredFields.every((field) => {
      const control = this.partnerRequestForm.get(field);
      return control?.valid;
    });
  }

  onSubmitStep1() {
    if (!this.isStep1Valid()) {
      // Mark step 1 fields as touched to show errors
      [
        'businessName',
        'country',
        'busines_type',
        'category',
        'otherIndustry',
        'emailAddress',
        'contactPersonFullName',
        'mobileNumber',
        'webURL',
        'platform',
        'otherPlatfom',
      ].forEach((field) => {
        this.partnerRequestForm.get(field)?.markAsTouched();
      });
      return;
    }

    this.submitting = true;

    const requestBody = {
      businessName: this.partnerRequestForm.get('businessName')?.value?.trim(),
      businessNameArabic: this.partnerRequestForm.get('businessNameArabic')?.value?.trim(),
      countryNameEn: this.partnerRequestForm.get('country')?.value?.nameEn?.trim(),
      countryId: this.partnerRequestForm.get('country')?.value?.isoCode,
      businessType: this.partnerRequestForm.get('busines_type')?.value?.nameEn?.trim(),
      industryId: this.partnerRequestForm.get('busines_type')?.value?.id,
      categoryNameEn: this.partnerRequestForm.get('category')?.value?.nameEn?.trim(),
      categoryId: this.partnerRequestForm.get('category')?.value?.id,
      otherIndustry: this.partnerRequestForm.get('otherIndustry')?.value,
      businessEmail: this.partnerRequestForm.get('emailAddress')?.value,
      platformNameEn: this.partnerRequestForm.get('platform')?.value?.nameEn?.trim(),
      platfomId: this.partnerRequestForm.get('platform')?.value?.id,
      url: this.partnerRequestForm.get('webURL')?.value,
      otherPlatfom: this.partnerRequestForm.get('otherPlatfom')?.value,
      // Contact Information (required)
      contactPersonFullName: this.partnerRequestForm.get('contactPersonFullName')?.value?.trim(),
      contactNumber: this.partnerRequestForm.get('mobileNumber')?.value,
    };

    this.partnerService.merchantSignUpV2(requestBody as PartnerRequest).subscribe({
      next: (resp) => {
        this.submitting = false;
        if (!resp) return;

        this.interestRequestId = resp.id;
        this.applicationEditToken = resp.editToken ?? null;
        this.activeStep = 1; // Move to Step 2

        this.messageService.add({
          severity: 'success',
          summary: 'Application Submitted',
          detail: 'Your application has been submitted. You can now add more details.',
        });
      },
      error: (err) => {
        this.submitting = false;

        let errorCode = err?.errorCode || err?.error?.errorCode;
        let errorMessage = err?.message || err?.error?.message;

        if (!errorCode && typeof err?.error === 'string') {
          try {
            const parsed = JSON.parse(err.error);
            errorCode = parsed.errorCode;
            errorMessage = parsed.message || errorMessage;
          } catch {
            // ignore: error body is not JSON; keep the default message
          }
        }

        const detailMsg = errorMessage || 'Email address or phone number already exists';

        // Display existing application Toast error message
        this.checkoutService.showMessage('error', detailMsg);

        if (errorCode !== 'T227') {
          this.transferSignal.setErrorPopup(true);
        }
      },
    });
  }

  // ========== Step 2: Update Form (Optional Fields) ==========

  onSubmitStep2() {
    if (!this.interestRequestId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No application ID found. Please start over.',
      });
      return;
    }

    this.updating = true;

    const updateBody = {
      editToken: this.applicationEditToken,
      brandName: this.partnerRequestForm.get('brandName')?.value?.trim(),
      contactPersonFullName: this.partnerRequestForm.get('contactPersonFullName')?.value?.trim(),
      contactNumber: this.partnerRequestForm.get('mobileNumber')?.value,
      categoryNameEn: this.partnerRequestForm.get('category')?.value?.nameEn?.trim(),
      categoryId: this.partnerRequestForm.get('category')?.value?.id,
      otherIndustry: this.partnerRequestForm.get('otherIndustry')?.value,
      bankName: this.partnerRequestForm.get('bankName')?.value?.trim(),
      bankAccountHolderName: this.partnerRequestForm.get('bankAccountHolderName')?.value?.trim(),
      bankAccountNumber: this.partnerRequestForm.get('bankAccountNumber')?.value?.trim(),
      iban: this.partnerRequestForm.get('iban')?.value?.trim(),
      comments: this.partnerRequestForm.get('comments')?.value,
      // Include image files if uploaded (matching edit-merchant pattern)
      logoFile: this.logoFile ? this.replaceImg(this.logoFile) : undefined,
      featuredImageFile: this.featuredImageFile
        ? this.replaceImg(this.featuredImageFile)
        : undefined,
    };

    this.partnerService.updateInterestRequest(this.interestRequestId, updateBody as any).subscribe({
      next: () => {
        this.updating = false;
        this.activeStep = 2; // Move to Step 3

        this.messageService.add({
          severity: 'success',
          summary: 'Details Saved',
          detail: 'Additional details have been saved.',
        });
      },
      error: (err) => {
        this.updating = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to save details. Please try again.',
        });
      },
    });
  }

  skipStep2() {
    this.activeStep = 2; // Skip to documents
  }

  // ========== Step 3: Documents (Optional) ==========

  onBasicUploadAuto(event: any, documentType: string, fileUpload?: any) {
    const file = event.files?.[0];
    if (!file || !this.interestRequestId) return;

    // Validate file format
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'];
    const allowedExtensions = ['.pdf', '.png', '.jpeg', '.jpg', '.gif'];
    const extension = file.name
      ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      : '';

    // Require an allowed extension, and an allowed MIME type when the browser reports one
    // (some systems leave file.type empty). Accepting either one let `x.exe` through as image/png.
    const isTypeAllowed = !file.type || allowedTypes.includes(file.type);

    if (!allowedExtensions.includes(extension) || !isTypeAllowed) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid file format. Only PDF, PNG, JPEG, and GIF files are allowed.',
      });
      if (fileUpload) {
        fileUpload.clear();
      }
      return;
    }

    const limit = 5000000; // 5 MB
    if (file.size > limit) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'File size exceeds the 5 MB limit.',
      });
      if (fileUpload) {
        fileUpload.clear();
      }
      return;
    }

    this.partnerService
      .uploadDocuments(this.interestRequestId, documentType, file, {
        editToken: this.applicationEditToken,
      })
      .subscribe({
        next: () => {
          this.uploadedFiles[documentType] = file.name;
          this.messageService.add({
            severity: 'success',
            summary: 'Uploaded',
            detail: 'Document uploaded successfully',
          });
        },
        error: (err) => {
          console.log('Upload Error:', err);

          let errorMessage = 'Upload failed. Please try again.';

          if (err) {
            if (err.message) {
              errorMessage = err.message;
            } else if (err.error) {
              if (typeof err.error === 'string') {
                try {
                  errorMessage = JSON.parse(err.error).message || err.error;
                } catch {
                  errorMessage = err.error;
                }
              } else {
                errorMessage = err.error.message || err.error.errors?.[0] || errorMessage;
              }
            }
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
          });

          if (fileUpload) {
            fileUpload.clear();
          }
        },
      });
  }

  submitDocuments() {
    this.transferSignal.setShowThankyouPopup(true);
    this.transferSignal.setShowSignUp(false);
  }

  skipStep3() {
    // Skip documents and show thank you
    this.transferSignal.setShowThankyouPopup(true);
    this.transferSignal.setShowSignUp(false);
  }

  // ========== Image Upload (matching edit-merchant pattern) ==========

  onFileSelected(event: Event, type: 'logoFile' | 'featuredImage'): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) return;

    const fileSizeKB = file.size / 1024;

    // Clear previous errors
    this.partnerRequestForm.get(type)?.setErrors(null);

    // Validate file type
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.partnerRequestForm.get(type)?.setErrors({
        fileType: {
          fileType: 'Invalid file type. Please select a PNG or JPG file.',
        },
      });
      return;
    }

    // Validate file size (500KB max)
    if (fileSizeKB > 500) {
      this.partnerRequestForm.get(type)?.setErrors({
        fileSizeExceeded: {
          fileSizeExceeded: 'File exceeds 500KB limit. Please select a smaller file.',
        },
      });
      return;
    }

    // Convert to base64 for preview and API
    this.convertToBase64(file, type);
  }

  convertToBase64(file: File, type: 'logoFile' | 'featuredImage'): void {
    const reader = new FileReader();
    reader.onload = () => {
      if (type === 'logoFile') {
        this.logoFile = reader.result as string;
      } else {
        this.featuredImageFile = reader.result as string;
      }
    };
    reader.readAsDataURL(file);
  }

  // Strip base64 prefix before sending to API (matching edit-merchant's replaceImg)
  replaceImg(img: string): string {
    return img?.replace(img?.slice(0, img?.indexOf(',') + 1), '').trim();
  }

  // ========== Navigation Control ==========

  /**
   * Determines if user can navigate to a specific step
   * - Step 0 (Application): Only accessible before submission
   * - Steps 1 & 2 (Additional/Documents): Only accessible after Step 0 is submitted
   */
  canNavigateToStep(stepIndex: number): boolean {
    if (stepIndex === 0) {
      // Can only access Step 1 if we haven't submitted yet
      return !this.interestRequestId;
    } else {
      // Can access Steps 2 & 3 only after Step 1 is submitted
      return !!this.interestRequestId;
    }
  }

  /**
   * Handles stepper header clicks with navigation restrictions
   */
  onStepHeaderClick(stepIndex: number, onClickEmitter: any): void {
    if (this.canNavigateToStep(stepIndex)) {
      onClickEmitter.emit();
    }
  }

  // ========== Utility Methods ==========

  showDialog(isTerm: boolean) {
    this.displayDialog = true;
    this.isTermOfUse = isTerm;
  }

  onKeyPress() {
    if (this.partnerRequestForm.get('emailAddress')?.valid) {
      if (this.elementRef.nativeElement.querySelector('.pi')) {
        this.elementRef.nativeElement.querySelector('.pi').style.display = 'none';
      }
    } else {
      if (this.elementRef.nativeElement.querySelector('.pi')) {
        this.elementRef.nativeElement.querySelector('.pi').style.display = 'block';
      }
    }
  }

  isInputNotEmpty(formCtrl: any): boolean {
    const ctrl = this.partnerRequestForm.get(formCtrl);
    const value = ctrl?.value ? String(ctrl.value).trim() : '';
    return value.length > 0;
  }
}
